import React, { ComponentProps, forwardRef, RefObject, useState } from 'react';
import classNames from 'classnames';

import { getFlexLayoutTransientProps, useMessageFormatter } from '@redsift/design-system';
import intlMessages from './intl';

import {
  Button,
  Comp,
  Flexbox,
  getStylingTransientProps,
  Heading,
  ThemeProvider,
  useId,
  useTheme,
  warnIfNoAccessibleLabelFound,
} from '@redsift/design-system';

import { ChartContainerProps } from './types';
import { StyledChartContainer, StyledChartContainerCaption, StyledChartContainerTitle } from './styles';

const COMPONENT_NAME = 'ChartContainer';
const CLASSNAME = 'redsift-chart-container';

export const ChartContainer: Comp<ChartContainerProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { transientProps: stylingTransientProps, otherProps: propsWithoutStyling } = getStylingTransientProps(props);
  const { transientProps: flexTransientProps, otherProps } = getFlexLayoutTransientProps(propsWithoutStyling);
  const transientProps = { ...stylingTransientProps, ...flexTransientProps };

  const {
    'aria-label': propsAriaLabel,
    'aria-labelledby': propsAriaLabelledby,
    description,
    descriptionPosition = 'belowChart',
    chartProps,
    chartRef,
    children,
    className,
    id: propsId,
    title,
    onReset,
    theme: propsTheme,
    mode = 'static',
    definition,
    interactionExplanation,
    descriptionForAssistiveTechnology,
    dataTableRepresentation,
    xAxisDefinition,
    yAxisDefinition,
    ...forwardedProps
  } = otherProps;
  const [_id] = useId();
  const id = propsId ?? _id;

  const theme = useTheme(propsTheme);
  const [viewAsDataTable, setViewAsDataTable] = useState(false);

  warnIfNoAccessibleLabelFound(props as ComponentProps<keyof JSX.IntrinsicElements>, [title]);

  const format = useMessageFormatter(intlMessages);

  const ariaLabel = propsAriaLabel ? propsAriaLabel : propsAriaLabelledby || title ? undefined : undefined;
  const ariaLabelledby = propsAriaLabelledby ? propsAriaLabelledby : title ? `id${id}__title` : undefined;

  return (
    <ThemeProvider value={{ theme }}>
      <figure>
        <div>
          <StyledChartContainer
            $theme={theme}
            role="region"
            aria-hidden="false"
            {...(ariaLabel ? { 'aria-label': `${ariaLabel}. ${format(mode)}.` } : {})}
            {...(ariaLabelledby ? { 'aria-labelledby': `${ariaLabelledby} id${id}__interactive-or-not` } : {})}
            {...forwardedProps}
            {...transientProps}
            ref={ref as RefObject<HTMLDivElement>}
            className={classNames(ChartContainer.className, className)}
            id={id}
          >
            <div id={`id${id}__screen-reader-region-before`} aria-hidden="false" style={{ position: 'relative' }}>
              <div
                aria-hidden="false"
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  clip: 'rect(1px, 1px, 1px, 1px)',
                  marginTop: '-3px',
                  opacity: '0.01',
                }}
              >
                <p id={`id${id}__interactive-or-not`} aria-hidden="true" style={{ display: 'none' }}>
                  {format(`${mode}-chart`)}
                </p>
                {ariaLabel ? (
                  <p>{`${ariaLabel}. ${format(`${mode}-chart`)}`}</p>
                ) : (
                  <p aria-labelledby={`${ariaLabelledby} id${id}__interactive-or-not`} />
                )}
                <div>{descriptionForAssistiveTechnology ?? description}</div>
                <div>{interactionExplanation}</div>
                <div>{definition}</div>
                {dataTableRepresentation ? (
                  <div>
                    <button
                      tabIndex={-1}
                      aria-expanded={viewAsDataTable}
                      onClick={() => setViewAsDataTable(!viewAsDataTable)}
                    >
                      {format('view-as-data-table')}
                      {', '}
                      {ariaLabel ? <span>{ariaLabel}</span> : <span aria-labelledby={ariaLabelledby} />}
                    </button>
                  </div>
                ) : null}
                <div>{xAxisDefinition}</div>
                <div>{yAxisDefinition}</div>
              </div>
            </div>
            <Flexbox flexDirection="column">
              {title || onReset ? (
                <ChartContainerTitle onReset={onReset} id={id}>
                  {title}
                </ChartContainerTitle>
              ) : null}
              {descriptionPosition === 'belowTitle' ? (
                <ChartContainerDescription id={id}>{description}</ChartContainerDescription>
              ) : null}
            </Flexbox>

            <Flexbox flexDirection="column">
              {children ? (
                <div
                  aria-hidden="false"
                  dir="ltr"
                  {...chartProps}
                  ref={chartRef}
                  className={`${ChartContainer.className}__content`}
                >
                  {children}
                </div>
              ) : null}
              {descriptionPosition === 'belowChart' ? (
                <ChartContainerDescription id={id}>{description}</ChartContainerDescription>
              ) : null}
              {viewAsDataTable && dataTableRepresentation ? (
                <table tabIndex={-1} summary={format('table-summary')}>
                  {ariaLabel ? <caption>{ariaLabel}</caption> : <caption aria-labelledby={ariaLabelledby} />}
                  {dataTableRepresentation.header}
                  {dataTableRepresentation.body}
                </table>
              ) : null}
            </Flexbox>
          </StyledChartContainer>
        </div>
      </figure>
    </ThemeProvider>
  );
});
ChartContainer.className = CLASSNAME;
ChartContainer.displayName = COMPONENT_NAME;

export const ChartContainerTitle: Comp<Pick<ChartContainerProps, 'onReset'> & ComponentProps<'div'>, HTMLDivElement> =
  forwardRef((props, ref) => {
    const { children, className, onReset, id, ...forwardedProps } = props;
    const theme = useTheme();
    const format = useMessageFormatter(intlMessages);

    return (
      <StyledChartContainerTitle
        aria-hidden="true"
        $theme={theme}
        alignItems="center"
        justifyContent="space-between"
        className={classNames(`${ChartContainer.className}__title`, className)}
        id={`id${id}__title`}
        {...forwardedProps}
        ref={ref}
      >
        {typeof children === 'string' ? <Heading as="h3">{children}</Heading> : children ? children : null}

        {onReset ? (
          <Button
            className={`${ChartContainer.className}-title__reset-button`}
            color="grey"
            variant="unstyled"
            onClick={onReset}
          >
            {format('reset')}
          </Button>
        ) : null}
      </StyledChartContainerTitle>
    );
  });
ChartContainerTitle.displayName = 'ChartContainerTitle';

export const ChartContainerDescription: Comp<ComponentProps<'div'>, HTMLDivElement> = forwardRef((props, ref) => {
  const { children, className, id, ...forwardedProps } = props;
  const theme = useTheme();

  return typeof children === 'string' ? (
    <StyledChartContainerCaption
      aria-hidden="true"
      $theme={theme}
      className={classNames(`${ChartContainer.className}__description`, className)}
      id={`id${id}__description`}
      {...forwardedProps}
      ref={ref}
    >
      {children}
    </StyledChartContainerCaption>
  ) : (
    <>{children}</>
  );
});
ChartContainerDescription.displayName = 'ChartContainerDescription';
